import React, { useState, useEffect, useCallback } from 'react';
import { WeatherStationEntityEntry } from '../../../domain/model/weatherStation';
import ContextHelp from '../../../easymap/ContextHelp/ContextHelp';
import {
    WEATHER_STATION_REGIONS, SensorConfiguration, SensorValidationResult } from '../../../types/weatherStation';
import SensorManager from './SensorManager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faTrash, faUndo, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './StationForm.scss';
import { useConfirm } from '../../common/AppConfirm';

/**
 * StationForm Component
 * 
 * Comprehensive form for creating and editing weather station records.
 * Integrates SensorManager for sensor configuration and provides full validation.
 * Handles both create and edit modes with appropriate field binding.
 */

// Props Interface
export interface StationFormProps {
    station?: WeatherStationEntityEntry;
    isEditing?: boolean;
    onSave: (station: Partial<WeatherStationEntityEntry>) => Promise<void>;
    onCancel: () => void;
    onDelete?: (stationId: string) => Promise<void>;
    disabled?: boolean;
    title?: string;
    onDirtyChange?: (dirty: boolean) => void;
}

// Form validation interface
interface FormValidation {
    isValid: boolean;
    errors: Record<string, string>;
    warnings: Record<string, string>;
}



export const StationForm: React.FC<StationFormProps> = ({
    station,
    isEditing = false,
    onSave,
    onCancel,
    onDelete,
    disabled = false,
    title,
    onDirtyChange
}) => {
    const askConfirm = useConfirm();
    // Helper function to convert sensor tokens from various formats to array
    const toArray = (value: string | string[] | undefined): string[] => {
        if (!value) return [];

        // Handle array
        if (Array.isArray(value)) {
            // Check if it's an array with a single element that contains space-separated tokens
            if (value.length === 1 && typeof value[0] === 'string' && value[0].includes(' ')) {
                return value[0].trim().split(/\s+/).filter(token => token.length > 0);
            }
            return value;
        }

        // Handle space-separated string (common database storage format)
        if (typeof value === 'string') {
            return value.trim().split(/\s+/).filter(token => token.length > 0);
        }

        return [value];
    };

    // Form state
    const [formData, setFormData] = useState<Partial<WeatherStationEntityEntry>>({
        nesdis: station?.nesdis || '',
        name: station?.name || '',
        region: station?.region || '',
        status: station?.status || 'Fully Operational',
        geometry: station?.geometry || { type: 'Point', coordinates: [0, 0] },
        elevationMeters: station?.elevationMeters || 0,
        contact: station?.contact || '',
        cffdrsMode: station?.cffdrsMode || 'off',
        fuelType: station?.fuelType || '',
        xmitTime: station?.xmitTime,
        sensorTokens: toArray(station?.sensorTokens),
        sensorArray: station?.sensorArray || []
    });

    const [sensorConfig, setSensorConfig] = useState<SensorConfiguration | null>(null);
    const [sensorValidation, setSensorValidation] = useState<SensorValidationResult>({ isValid: true, errors: [], warnings: [] });
    const [formValidation, setFormValidation] = useState<FormValidation>({ isValid: true, errors: {}, warnings: {} });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const initialFormData = React.useRef(formData);
    const [dirty, setDirty] = useState(false);

    // Convert coordinates for display
    const [latitude, setLatitude] = useState<string>(
        station?.geometry?.coordinates?.[1]?.toString() || '0'
    );
    const [longitude, setLongitude] = useState<string>(
        station?.geometry?.coordinates?.[0]?.toString() || '0'
    );

    // Convert xmitTime for form input
    const [xmitTimeString, setXmitTimeString] = useState<string>(() => {
        if (!station?.xmitTime) return '';
        if (station.xmitTime instanceof Date) {
            return station.xmitTime.toTimeString().slice(0, 5);
        }
        return '';
    });

    // NESDIS ID validation per NOAA GOES DCS Certification Standard V2.0
    function validateGOESNesdisId(nesdisId: string) {
        const cleaned = String(nesdisId).replace(/\s+/g, '').toUpperCase();
        const hexPattern = /^[0-9A-F]{8}$/;
        if (!hexPattern.test(cleaned)) {
            return {
                valid: false,
                error: 'NESDIS ID must be exactly 8 hexadecimal characters (0-9, A-F)',
                formatted: null
            };
        }
        const idValue = parseInt(cleaned, 16);
        if ((idValue & 1) !== 0) {
            return {
                valid: false,
                error: 'Invalid NESDIS ID: the least significant bit (bit 0) must be 0',
                formatted: null
            };
        }
        const formatted = cleaned.match(/.{2}/g)?.join(' ') || cleaned;
        return {
            valid: true,
            error: null,
            formatted
        };
    }

    // Validation rules
    const validateForm = useCallback((): FormValidation => {
        const errors: Record<string, string> = {};
        const warnings: Record<string, string> = {};

        // NESDIS validation
        if (!formData.nesdis?.trim()) {
            errors.nesdis = 'Station ID (NESDIS) is required';
        } else {
            const nesdisResult = validateGOESNesdisId(formData.nesdis);
            if (!nesdisResult.valid) {
                errors.nesdis = nesdisResult.error || 'Invalid NESDIS ID';
            }
        }

        if (!formData.name?.trim()) {
            errors.name = 'Station name is required';
        } else if (formData.name.length < 3) {
            errors.name = 'Station name must be at least 3 characters';
        }

        if (!formData.region) {
            errors.region = 'Region is required';
        }

        // Email validation
        if (formData.contact && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact)) {
            errors.contact = 'Invalid email format';
        }

        // Coordinate validation
        const lat = parseFloat(latitude);
        const lon = parseFloat(longitude);

        if (isNaN(lat) || lat < -90 || lat > 90) {
            errors.latitude = 'Latitude must be between -90 and 90';
        }

        if (isNaN(lon) || lon < -180 || lon > 180) {
            errors.longitude = 'Longitude must be between -180 and 180';
        }

        // Elevation validation
        if (formData.elevationMeters !== undefined && formData.elevationMeters < -500) {
            warnings.elevation = 'Elevation below sea level - please verify';
        }

        // Transmission time validation
        if (xmitTimeString && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(xmitTimeString)) {
            errors.xmitTime = 'Time must be in HH:MM format (24-hour)';
        }

        return {
            isValid: Object.keys(errors).length === 0 && sensorValidation.isValid,
            errors,
            warnings
        };
    }, [formData, latitude, longitude, xmitTimeString, sensorValidation]);

    // Update form validation and dirty state when form data changes
    useEffect(() => {
        const validation = validateForm();
        setFormValidation(validation);
        // Dirty check: compare formData to initialFormData.current
        const isDirty = JSON.stringify(formData) !== JSON.stringify(initialFormData.current);
        setDirty(isDirty);
    }, [validateForm, formData]);

    // Notify parent of dirty state
    useEffect(() => {
        if (typeof onDirtyChange === 'function') {
            onDirtyChange(dirty);
        }
    }, [dirty, onDirtyChange]);

    // Handle input changes
    const handleInputChange = useCallback((field: keyof WeatherStationEntityEntry, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    // Handle coordinate changes
    const handleCoordinateChange = useCallback((coord: 'latitude' | 'longitude', value: string) => {
        if (coord === 'latitude') {
            setLatitude(value);
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                handleInputChange('geometry', {
                    type: 'Point',
                    coordinates: [parseFloat(longitude) || 0, numValue]
                });
            }
        } else {
            setLongitude(value);
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                handleInputChange('geometry', {
                    type: 'Point',
                    coordinates: [numValue, parseFloat(latitude) || 0]
                });
            }
        }
    }, [latitude, longitude, handleInputChange]);

    // Handle sensor configuration changes
    const handleSensorConfigChange = useCallback((config: SensorConfiguration) => {
        setSensorConfig(config);
        // Update form data with sensor configuration, but do NOT submit or call onSave here
        handleInputChange('sensorTokens', config.sensorTokens);
        handleInputChange('sensorArray', config.sensorArray);
        // Debug: log sensor config changes
        console.log('[StationForm] handleSensorConfigChange called. New config:', config);
    }, [handleInputChange]);

    // Handle sensor validation changes
    const handleSensorValidationChange = useCallback((validation: SensorValidationResult) => {
        setSensorValidation(validation);
    }, []);
    // Handle form submission
    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formValidation.isValid || isSubmitting) {
            console.log('[StationForm] handleSubmit: invalid or submitting, abort.');
            return;
        }

        setIsSubmitting(true);
        try {
            const submissionData = {
                ...formData,
                geometry: {
                    type: 'Point' as const,
                    coordinates: [parseFloat(longitude), parseFloat(latitude)] as [number, number]
                },
                wkt: `POINT(${parseFloat(longitude)} ${parseFloat(latitude)})`,
                //              sensorTokens: formData.sensorTokens?.join(" "),
                sensorTokens: Array.isArray(formData.sensorTokens)
                    ? formData.sensorTokens.join(" ")
                    : formData.sensorTokens,
                xmitTime: xmitTimeString ? new Date(`1970-01-01T${xmitTimeString}:00`) : undefined
            };

            console.log('[StationForm] handleSubmit: calling onSave with', submissionData);
            await onSave(submissionData);
        } catch (error) {
            console.error('Form submission error:', error);
            // Handle error display here if needed
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, formValidation.isValid, isSubmitting, latitude, longitude, xmitTimeString, onSave]);

    // Handle delete
    const handleDelete = useCallback(async () => {
        if (!station?.id || !onDelete || isSubmitting) {
            return;
        }

        if (await askConfirm({ title: 'Delete station', message: `Are you sure you want to delete station "${station.name}"? This action cannot be undone.`, confirmLabel: 'Delete', destructive: true })) {
            setIsSubmitting(true);
            try {
                await onDelete(String(station.id));
            } catch (error) {
                console.error('Delete error:', error);
            } finally {
                setIsSubmitting(false);
            }
        }
    }, [station, onDelete, isSubmitting]);

    // Handle form reset
    const handleReset = useCallback(() => {
        if (station) {
            setFormData({
                nesdis: station.nesdis || '',
                name: station.name || '',
                region: station.region || '',
                status: station.status || 'Fully Operational',
                geometry: station.geometry || { type: 'Point', coordinates: [0, 0] },
                elevationMeters: station.elevationMeters || 0,
                contact: station.contact || '',
                cffdrsMode: station.cffdrsMode || 'off',
                fuelType: station.fuelType || '',
                xmitTime: station.xmitTime,
                sensorTokens: toArray(station.sensorTokens),
                sensorArray: station.sensorArray || []
            });
            setLatitude(station.geometry?.coordinates?.[1]?.toString() || '0');
            setLongitude(station.geometry?.coordinates?.[0]?.toString() || '0');
            setXmitTimeString(() => {
                if (!station.xmitTime) return '';
                if (station.xmitTime instanceof Date) {
                    return station.xmitTime.toTimeString().slice(0, 5);
                }
                return '';
            });
        } else {
            // Reset to empty form
            setFormData({
                nesdis: '',
                name: '',
                region: '',
                status: 'Fully Operational',
                geometry: { type: 'Point', coordinates: [0, 0] },
                elevationMeters: 0,
                contact: '',
                cffdrsMode: 'off',
                fuelType: '',
                xmitTime: undefined,
                sensorTokens: [],
                sensorArray: []
            });
            setLatitude('0');
            setLongitude('0');
            setXmitTimeString('');
        }
    }, [station]);


    return (
        <div className="station-form-container">
            {/* Header */}
            <div className="station-form-header">
                <h2 className="station-form-title">
                    {title || (isEditing ? `Edit Station: ${station?.name}` : 'Create New Station')}
                    <ContextHelp title='Help with Station Form' context='station-form-help' />
                </h2>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Basic Information */}
                <div className="station-form-grid">
                    <div className="station-form-group">
                        <label className="station-form-label station-form-required-label">
                            Station ID (NESDIS) *
                        </label>
                        <input
                            className={`station-form-input${formValidation.errors.nesdis ? ' station-form-input-error' : ''}`}
                            type="text"
                            value={formData.nesdis || ''}
                            onChange={(e) => handleInputChange('nesdis', e.target.value.toUpperCase())}
                            placeholder="e.g., CE1200B8"
                            maxLength={8}
                            disabled={disabled}
                        />
                        {formValidation.errors.nesdis && (
                            <span className="station-form-error-message">{formValidation.errors.nesdis}</span>
                        )}
                        {!formValidation.errors.nesdis && formData.nesdis && validateGOESNesdisId(formData.nesdis).formatted && (
                            <span className="station-form-help-text">Formatted: {validateGOESNesdisId(formData.nesdis).formatted}</span>
                        )}
                        <span className="station-form-help-text">8-character NESDIS hex ID (e.g., CE1200B8, LSB must be 0)</span>
                    </div>

                    <div className="station-form-group">
                        <label className="station-form-label station-form-required-label">
                            Station Name *
                        </label>
                        <input
                            className={`station-form-input${formValidation.errors.name ? ' station-form-input-error' : ''}`}
                            type="text"
                            value={formData.name || ''}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="e.g., Mountain View Station"
                            disabled={disabled}
                        />
                        {formValidation.errors.name && (
                            <span className="station-form-error-message">{formValidation.errors.name}</span>
                        )}
                    </div>

                    <div className="station-form-group">
                        <label className="station-form-label station-form-required-label">
                            Region *
                        </label>
                        <select
                            className={`station-form-select${formValidation.errors.region ? ' station-form-input-error' : ''}`}
                            value={formData.region || ''}
                            onChange={(e) => handleInputChange('region', e.target.value)}
                            disabled={disabled}
                        >
                            <option value="">Select Region</option>
                            {WEATHER_STATION_REGIONS.map(region => (
                                <option key={region.code} value={region.code}>
                                    {region.label} ({region.code})
                                </option>
                            ))}
                        </select>
                        {formValidation.errors.region && (
                            <span className="station-form-error-message">{formValidation.errors.region}</span>
                        )}
                    </div>

                    <div className="station-form-group">
                        <label className="station-form-label">Status</label>
                        <select
                            className="station-form-select"
                            value={formData.status || 'Fully Operational'}
                            onChange={(e) => handleInputChange('status', e.target.value)}
                            disabled={disabled}
                        >
                            <option value="Fully Operational">Fully Operational</option>
                            <option value="Demobilized">Demobilized</option>
                            <option value="Maintenance">Maintenance</option>
                        </select>
                    </div>
                </div>

                {/* Location Information */}
                <div className="station-form-grid">
                    <div className="station-form-group">
                        <label className="station-form-label">Latitude</label>
                        <input
                            className={`station-form-input${formValidation.errors.latitude ? ' station-form-input-error' : ''}`}
                            type="number"
                            step="0.000001"
                            value={latitude}
                            onChange={(e) => handleCoordinateChange('latitude', e.target.value)}
                            placeholder="e.g., 62.4540"
                            disabled={disabled}
                        />
                        {formValidation.errors.latitude && (
                            <span className="station-form-error-message">{formValidation.errors.latitude}</span>
                        )}
                        <span className="station-form-help-text">Decimal degrees (-90 to 90)</span>
                    </div>

                    <div className="station-form-group">
                        <label className="station-form-label">Longitude</label>
                        <input
                            className={`station-form-input${formValidation.errors.longitude ? ' station-form-input-error' : ''}`}
                            type="number"
                            step="0.000001"
                            value={longitude}
                            onChange={(e) => handleCoordinateChange('longitude', e.target.value)}
                            placeholder="e.g., -114.3718"
                            disabled={disabled}
                        />
                        {formValidation.errors.longitude && (
                            <span className="station-form-error-message">{formValidation.errors.longitude}</span>
                        )}
                        <span className="station-form-help-text">Decimal degrees (-180 to 180)</span>
                    </div>

                    <div className="station-form-group">
                        <label className="station-form-label">Elevation (meters)</label>
                        <input
                            className={`station-form-input${formValidation.warnings.elevation ? ' station-form-input-warning' : ''}`}
                            type="number"
                            value={formData.elevationMeters || 0}
                            onChange={(e) => handleInputChange('elevationMeters', parseInt(e.target.value) || 0)}
                            placeholder="e.g., 350"
                            disabled={disabled}
                        />
                        {formValidation.warnings.elevation && (
                            <span className="station-form-warning-message">{formValidation.warnings.elevation}</span>
                        )}
                    </div>

                    <div className="station-form-group">
                        <label className="station-form-label">Contact Email</label>
                        <input
                            className={`station-form-input${formValidation.errors.contact ? ' station-form-input-error' : ''}`}
                            type="email"
                            value={formData.contact || ''}
                            onChange={(e) => handleInputChange('contact', e.target.value)}
                            placeholder="e.g., contact@example.com"
                            disabled={disabled}
                        />
                        {formValidation.errors.contact && (
                            <span className="station-form-error-message">{formValidation.errors.contact}</span>
                        )}
                    </div>
                </div>

                {/* CFFDRS and Operations */}
                <div className="station-form-grid">
                    <div className="station-form-group">
                        <label className="station-form-label">CFFDRS Mode</label>
                        <select
                            className="station-form-select"
                            value={formData.cffdrsMode || 'off'}
                            onChange={(e) => handleInputChange('cffdrsMode', e.target.value)}
                            disabled={disabled}
                        >
                            <option value="off">Off</option>
                            <option value="spring">Spring</option>
                            <option value="summer">Summer</option>
                        </select>
                    </div>

                    <div className="station-form-group">
                        <label className="station-form-label">Fuel Type</label>
                        <input
                            className="station-form-input"
                            type="text"
                            value={formData.fuelType || ''}
                            onChange={(e) => handleInputChange('fuelType', e.target.value)}
                            placeholder="e.g., C2, M1"
                            disabled={disabled}
                        />
                        <span className="station-form-help-text">Optional CFFDRS fuel type code</span>
                    </div>

                    <div className="station-form-group">
                        <label className="station-form-label">Transmission Time</label>
                        <input
                            className={`station-form-input${formValidation.errors.xmitTime ? ' station-form-input-error' : ''}`}
                            type="time"
                            value={xmitTimeString}
                            onChange={(e) => setXmitTimeString(e.target.value)}
                            disabled={disabled}
                        />
                        {formValidation.errors.xmitTime && (
                            <span className="station-form-error-message">{formValidation.errors.xmitTime}</span>
                        )}
                        <span className="station-form-help-text">24-hour format</span>
                    </div>
                </div>

                {/* Sensor Configuration */}
                <div className="station-form-sensor-section">
                    <h3 className="station-form-sensor-section-title">Sensor Configuration</h3>
                    <SensorManager
                        //initialSensorTokens={formData.sensorTokens || []}
                        initialSensorTokens={toArray(formData.sensorTokens)}
                        initialSensorArray={formData.sensorArray || []}
                        onConfigurationChange={handleSensorConfigChange}
                        onValidationChange={handleSensorValidationChange}
                        disabled={disabled}
                        maxSensors={50}
                        showPreview={true}
                    />
                </div>

                {/* Action Bar */}
                <div className="station-form-action-bar">
                    <div className="station-form-action-group">
                        <button
                            type="submit"
                            className={`station-form-button${formValidation.isValid && !isSubmitting ? ' station-form-button-primary' : ' station-form-button-disabled'}`}
                            disabled={!formValidation.isValid || isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faSave} />
                                    {isEditing ? 'Update Station' : 'Create Station'}
                                </>
                            )}
                        </button>

                        <button
                            type="button"
                            className="station-form-button station-form-button-secondary"
                            onClick={handleReset}
                            disabled={disabled || isSubmitting}
                        >
                            <FontAwesomeIcon icon={faUndo} />
                            Reset
                        </button>
                    </div>

                    <div className="station-form-action-group">
                        {isEditing && onDelete && (
                            <button
                                type="button"
                                className="station-form-button station-form-button-danger"
                                onClick={handleDelete}
                                disabled={disabled || isSubmitting}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                Delete Station
                            </button>
                        )}

                        <button
                            type="button"
                            className="station-form-button station-form-button-secondary"
                            onClick={async () => {
                                if (dirty) {
                                    if (await askConfirm({ title: 'Unsaved changes', message: 'You have unsaved changes. Are you sure you want to exit?', confirmLabel: 'Discard changes', destructive: true })) {
                                        onCancel();
                                    }
                                } else {
                                    onCancel();
                                }
                            }}
                            disabled={isSubmitting}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                            Cancel
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default StationForm;